import React from "react";
import { Button, Select } from "antd";
import { Plus } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { ICategory } from "../../../types/api/category";
import { getCategoryColumns } from "./Categorycolumns";

// "" (no sortBy sent) now means alphabetical by default - the same
// listCategories call this table uses also backs the product form's
// Category/Sub Category dropdowns, which never pass a sortBy at all
// (Frankie, Sept 2026: dropdowns should default to A-Z). "Newest First"
// needs its own explicit value now rather than colliding with that
// default.
const SORT_OPTIONS = [
  { label: "Name: A-Z", value: "" },
  { label: "Sort: Newest First", value: "newest" },
  { label: "Name: Z-A", value: "name_desc" },
  { label: "Most Recently Added To", value: "recently_added_to" },
  { label: "By Content Amount", value: "content_amount" },
];

interface CategoryPanelProps {
  title: React.ReactNode;
  addLabel: string;
  isParent: boolean;
  list: ICategory[];
  total: number;
  loading: boolean;
  isError: boolean;
  queryData: any;
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
  sortBy?: string;
  onSortChange?: (v: string) => void;
  onPageChange: (page: number, limit: number) => void;
  onAdd: () => void;
  addDisabled?: boolean;
  emptyText?: string;
  selectedId?: string | null;
  onRowClick?: (record: ICategory) => void;
  onEdit: (record: ICategory) => void;
  onDelete: (id: string) => void;
}

/**
 * Renders one category grid — used for BOTH the parent table and the
 * child (subcategory) table. The only things that differ between the
 * two calls are the props passed in, not the component itself.
 */
export const CategoryPanel: React.FC<CategoryPanelProps> = ({
  title,
  addLabel,
  isParent,
  list,
  total,
  loading,
  isError,
  queryData,
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  sortBy = "",
  onSortChange,
  onPageChange,
  onAdd,
  addDisabled,
  emptyText,
  selectedId,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const columns = getCategoryColumns({
    isParent,
    selectedId,
    onEdit,
    onDelete,
  });

  return (
    <DataTable
      title={title}
      headerActions={
        <Button
          type="primary"
          size="small"
          icon={<Plus size={16} />}
          onClick={onAdd}
          disabled={addDisabled}
          className="!bg-primary flex items-center gap-2"
        >
          {addLabel}
        </Button>
      }
      dataSource={list}
      columns={columns}
      loading={loading}
      isError={isError}
      rowKey="_id"
      emptyText={emptyText}
      tableProps={
        onRowClick
          ? {
              onRow: (record: ICategory) => ({
                onClick: () => onRowClick(record),
                className: `cursor-pointer transition-colors ${selectedId === record._id ? "bg-blue-50" : "hover:bg-slate-50"}`,
              }),
            }
          : undefined
      }
      searchProps={{
        searchValue: search,
        onSearchChange,
        placeholder: isParent ? "Search parents..." : "Search subcategories...",
        statusValue: statusFilter,
        onStatusChange,
        children: onSortChange ? (
          <Select
            value={sortBy}
            onChange={onSortChange}
            options={SORT_OPTIONS}
            className="w-52 h-[35px]"
          />
        ) : undefined,
      }}
      paginationProps={
        queryData
          ? {
              page: queryData.page ?? 1,
              limit: queryData.limit ?? 10,
              total,
              onChange: (page: number, pageSize: number) => onPageChange(page, pageSize),
            }
          : undefined
      }
    />
  );
};
